import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Factory, Target, TrendingUp, CheckCircle2, Clock, PlayCircle,
  Cpu, Package, Truck, XOctagon, FlaskConical, Wheat, Boxes, Users, RefreshCw,
} from "lucide-react";
import BranchTeamPanel from "@/components/hr/BranchTeamPanel";
import ManufacturingOpsPanel from "@/components/mfg/ManufacturingOpsPanel";

export default function FactoryDashboardPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Auto-pick user's branch if factory_admin; else HO or first available
    if (branches.length && !branchId) {
      const userBid = user?.branch_id;
      if (userBid) setBranchId(userBid);
      else {
        const factory = branches.find((b) => b.type === "factory" && b.is_active !== false) || branches.find((b) => b.type === "factory") || branches[0];
        if (factory) setBranchId(factory.id);
      }
    }
  }, [branches, branchId, user]);

  const load = async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const r = await api.get(`/mfg/dashboard/factory?branch_id=${branchId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!branchId) return;
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [branchId]);

  const isFactoryScoped = !!user?.branch_id;

  return (
    <div className="space-y-6" data-testid="factory-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Manufacturing · Factory View</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Factory Dashboard</h1>
          <p className="text-sm text-[#8B7355] mt-1">Real-time shop-floor snapshot — production, machines, dispatch, QC status. Auto-refreshes every minute.</p>
        </div>
        <div className="flex items-center gap-2">
          {!isFactoryScoped && (
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-64" data-testid="factory-branch-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
              <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="factory-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {isFactoryScoped && (
        <Badge className="bg-blue-50 text-blue-800 border-blue-200 border" data-testid="factory-scope-badge">Scoped to your assigned factory</Badge>
      )}

      {!data ? (
        <div className="text-center py-24 text-[#8B7355]">
          <Factory className="w-10 h-10 mx-auto mb-2 opacity-40" />
          {loading ? "Loading factory metrics…" : branchId ? "No data yet." : "Select a factory to view the dashboard."}
        </div>
      ) : (
        <>
          {/* Section: Today's Plan Snapshot */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Today's Plan</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiTile label="Total Plans" value={data.today_plan} icon={Target} tone="neutral" testid="kpi-today-plan" />
              <KpiTile label="Pending" value={data.production_pending} icon={Clock} tone="slate" testid="kpi-production-pending" />
              <KpiTile label="In Progress" value={data.production_in_progress} icon={PlayCircle} tone="blue" testid="kpi-production-in-progress" />
              <KpiTile label="Completed" value={data.production_completed} icon={CheckCircle2} tone="emerald" testid="kpi-production-completed" />
            </div>
          </section>

          {/* Section: Target vs Actual */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Output & Efficiency</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiTile label="Target Qty" value={Math.round(data.target_qty || 0)} icon={Target} tone="amber" testid="kpi-target-qty" />
              <KpiTile label="Actual Qty" value={Math.round(data.actual_qty || 0)} icon={TrendingUp} tone="emerald" testid="kpi-actual-qty" />
              <div className={`border rounded-lg p-4 ${(data.efficiency_pct || 0) >= 90 ? "bg-emerald-50 border-emerald-200" : (data.efficiency_pct || 0) >= 70 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`} data-testid="kpi-efficiency">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#5C4033]">
                  <TrendingUp className="w-3.5 h-3.5" /> Efficiency
                </div>
                <div className="text-3xl font-[Outfit] font-semibold mt-1">{data.efficiency_pct ?? 0}%</div>
                <div className="mt-1.5 h-1.5 bg-white/60 rounded overflow-hidden">
                  <div className={`h-full ${(data.efficiency_pct || 0) >= 90 ? "bg-emerald-500" : (data.efficiency_pct || 0) >= 70 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.min(100, data.efficiency_pct || 0)}%` }} />
                </div>
              </div>
            </div>
          </section>

          {/* Section: Machines */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Machine Status</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiTile label="Total Machines" value={data.machines?.total || 0} icon={Cpu} tone="neutral" testid="kpi-machines-total" />
              <KpiTile label="Running" value={data.machines?.running || 0} icon={PlayCircle} tone="emerald" testid="kpi-machines-running" />
              <KpiTile label="Idle" value={data.machines?.idle || 0} icon={Clock} tone="slate" testid="kpi-machines-idle" />
              <KpiTile label="Breakdown" value={data.machines?.breakdown || 0} icon={XOctagon} tone="red" testid="kpi-machines-breakdown" />
              <KpiTile label="Maintenance" value={data.machines?.maintenance || 0} icon={Cpu} tone="amber" testid="kpi-machines-maintenance" />
            </div>
          </section>

          {/* Section: Downstream & Support */}
          <section>
            <h2 className="uc-label text-[#5C4033] mb-3">Downstream & Support</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiTile label="FG Batches Today" value={data.fg_batches_today || 0} icon={Package} tone="emerald" testid="kpi-fg-batches" />
              <KpiTile label="Dispatches Today" value={data.dispatches_today || 0} icon={Truck} tone="blue" testid="kpi-dispatches" />
              <KpiTile label="QC Pending" value={data.qc_pending_batches || 0} icon={FlaskConical} tone="amber" testid="kpi-qc-pending" />
              <KpiTile label="Rejected Today" value={data.rejected_today || 0} icon={XOctagon} tone="red" testid="kpi-rejected" />
              <KpiTile label="Raw Material SKUs" value={data.raw_material_lines || 0} icon={Wheat} tone="neutral" testid="kpi-rm" />
              <KpiTile label="Packaging SKUs" value={data.packaging_lines || 0} icon={Boxes} tone="neutral" testid="kpi-pkg" />
              <KpiTile label="Manpower Present" value={data.manpower_present || 0} icon={Users} tone="emerald" testid="kpi-manpower" />
            </div>
          </section>

          <div className="text-[10px] text-[#8B7355] text-right">
            As of {data.as_of ? new Date(data.as_of).toLocaleString() : "—"}
          </div>

          {/* Manufacturing Operations — WIP + DPR + Delays */}
          <section className="pt-4">
            <ManufacturingOpsPanel branchId={branchId} branchName={branches.find((b) => b.id === branchId)?.name} allowLogging={true} />
          </section>

          {/* Team view — HR data scoped to this factory */}
          <section className="pt-4">
            <BranchTeamPanel branchId={branchId} branchName={branches.find((b) => b.id === branchId)?.name} />
          </section>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone, testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    neutral: "bg-white border-[#EAE5D9] text-[#2C1810]",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-3xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
